import React from 'react';
import { IconListDemand, IconClock, IconEditProfile, IconAttachment } from '../../icons';
import { IProposta } from '../../../utility/IProposta';
import {
  Container,
  LeftSection,
  RightSection,
  TitleSection,
  MainTitle,
  ActionButton,
  IconWrapper,
  ChipContainer,
  Chip,
  ChipIcon,
  ChipText,
  ChipTextDeadline,
  ChipDot,
  HeaderSection,
  ContentSection,
  SectionDivider
} from './styles';
import { comum } from "../../../styles/Themes/light";
import { getStatusDotColor, getStatusBgColor } from '../../../utility/statusColors';
import { formatDateTimeWithBrasiliaConversion } from '../../../utility/timezoneUtils';
import { Link } from 'react-router-dom';

interface CabecalhoCardDemandaProps {
  proposta: IProposta;
  showDivider?: boolean;
}

export default function CabecalhoCardDemanda({ proposta, showDivider = false }: CabecalhoCardDemandaProps) {

  const getStatusColor = (status: string | undefined) => getStatusDotColor(status);

  const formatTime = (time: string) => {
    if (!time) return '';
    // suporta valores 'YYYY-MM-DD HH:mm:ss' ou ISO
    const match = time.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]) - 1;
      const day = Number(match[3]);
      const hour = Number(match[4]);
      const minute = Number(match[5]);
      const second = match[6] ? Number(match[6]) : 0;
      const local = new Date(year, month, day, hour, minute, second);
      return local.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    const dateObj = new Date(time);
    return isNaN(dateObj.getTime()) ? '' : dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date: string) => {
    if (!date) return '';
    const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]) - 1;
      const day = Number(match[3]);
      const local = new Date(year, month, day);
      return local.toLocaleDateString('pt-BR');
    }
    const dateObj = new Date(date);
    return isNaN(dateObj.getTime()) ? '' : dateObj.toLocaleDateString('pt-BR');
  };

  const getFileCount = () => {
    return proposta.qtd_arquivos || 0;
  };

  const normalizeText = (text: string) =>
    text
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();

  const getServiceTypeLabel = () => {
    const base = (proposta.descricao_categoria_servico || proposta.titulo_servico || '').trim();
    if (!base) return '';
    const normalized = normalizeText(base);

    if (normalized.includes('audien')) return 'Audiência';
    if (normalized.includes('diligen')) return 'Diligência';
    if (normalized.includes('petic')) return 'Petição';
    if (normalized.includes('protocol')) return 'Protocolo';
    if (normalized.includes('outros') || normalized.includes('outro')) return 'Outros';
    return base;
  };

  const serviceTypeLabel = getServiceTypeLabel();

  return (
    <Container>
      {/* Header Section */}
      <HeaderSection>
        <IconWrapper>
          <IconListDemand color={comum.colors.interfaceGray300} />
        </IconWrapper>
        
        <TitleSection>
          <MainTitle>DADOS DA DEMANDA</MainTitle>
        </TitleSection>
      </HeaderSection>

      {/* Content Section */}
      <ContentSection>        
        <LeftSection>
          <ChipContainer>
            {/* Demand Code */}
            <Chip variant="code">
              <ChipText>#{proposta.id_proposta}</ChipText>
            </Chip>

            {/* Deadline */}
            {proposta.data && (
              <Chip variant="deadline">
                <ChipIcon>
                  <IconClock color="#54666F" />
                </ChipIcon>
                <ChipTextDeadline>
                  Prazo fatal: {formatDateTimeWithBrasiliaConversion(proposta.data, proposta.data_tz)}
                </ChipTextDeadline>
              </Chip>
            )}

            {/* Service Type (Audiência, Diligência, Protocolo, Outros) */}
            {serviceTypeLabel && (
              <Chip variant="audience">
                <ChipText>{serviceTypeLabel}</ChipText>
              </Chip>
            )}

            {/* Modality */}
            {proposta.modalidade && (
              <Chip variant="modality">
                <ChipText>{proposta.modalidade === 'online' ? 'Virtual' : proposta.modalidade}</ChipText>
              </Chip>
            )}

            {/* Files */}
            <Chip variant="files">
              <ChipIcon>
                <IconAttachment color="#54666F" />
              </ChipIcon>
              <ChipText>{getFileCount()} arquivo{getFileCount() !== 1 ? 's' : ''}</ChipText>
            </Chip>

            {/* Status */}
            <Chip variant="status" style={{ backgroundColor: getStatusBgColor(proposta.status) }}>
              <ChipDot color={getStatusColor(proposta.status)} />
              <ChipText>{proposta.status || 'Pendente'}</ChipText>
            </Chip>
          </ChipContainer>
        </LeftSection>

        <RightSection>
          <ActionButton as={Link} to={`/demandas/${proposta.id_proposta}`}>
            <IconEditProfile color={comum.colors.interfacePure0} />
          </ActionButton>
        </RightSection>
      </ContentSection>
      {showDivider && <SectionDivider />}
    </Container>
  );
}
